import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  Calendar, 
  FileText, 
  Settings, 
  LogOut,
  Activity,
  UserCircle,
  History,
  CalendarDays,
  ClipboardList,
  Shield,
  AlertTriangle,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Page-level Error Boundary - keeps sidebar visible
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Page error:', error, errorInfo);
  }

  componentDidUpdate(prevProps) {
    // Reset error when navigating to a different page
    if (prevProps.locationKey !== this.props.locationKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-8 max-w-md text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-slate-900 mb-2">Erro ao carregar página</h2>
            <p className="text-sm text-slate-600 mb-4">Ocorreu um erro ao exibir esta página.</p>
            <button
              onClick={() => {
                this.setState({ hasError: false });
                window.location.reload();
              }}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors inline-flex items-center gap-2"
              data-testid="page-error-reload"
            >
              <RefreshCw className="h-4 w-4" />
              Recarregar
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export const Layout = ({ children }) => {
  const { user, logout, isAdmin, isMaster, isAdminGeral, isMarcadorGeral, isRelator, canViewSlots, canViewReports, canViewAudit, canManageUsers, canViewSettings, canViewUnits, canViewProcedimentos, canViewLicenses, canViewDashboard, canViewPatients, canViewAppointments, hasFullAccess, licenseStatus } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [clientLogoUrl, setClientLogoUrl] = useState(null);
  const [currentDateTime, setCurrentDateTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentDateTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Check if a client logo exists
    fetch(`${BACKEND_URL}/api/settings/logo/info`)
      .then(res => res.json())
      .then(data => {
        if (data.has_logo) {
          setClientLogoUrl(`${BACKEND_URL}/api/settings/logo?t=${Date.now()}`);
        }
      })
      .catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleLabel = () => {
    const labels = {
      master: 'Master',
      admin_geral: 'Administrador Geral',
      admin: 'Administrador',
      marcador_geral: 'Marcador Geral',
      marcador: 'Marcador',
      relator_hospital: 'Relator Hospital',
      relator_lab_municipal: 'Relator Lab. Municipal'
    };
    return labels[user?.role] || user?.role;
  };

  const getRoleBadgeColor = () => {
    const colors = {
      master: 'bg-purple-100 text-purple-700',
      admin_geral: 'bg-indigo-100 text-indigo-700',
      admin: 'bg-teal-100 text-teal-700',
      marcador_geral: 'bg-blue-100 text-blue-700',
      marcador: 'bg-slate-100 text-slate-600',
      relator_hospital: 'bg-emerald-100 text-emerald-700',
      relator_lab_municipal: 'bg-amber-100 text-amber-700'
    };
    return colors[user?.role] || 'bg-slate-100 text-slate-600';
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: LayoutDashboard, show: canViewDashboard() },
    { path: '/units', label: 'Unidades', icon: Building2, show: canViewUnits() },
    { path: '/patients', label: 'Pacientes', icon: UserCircle, show: canViewPatients() },
    { path: '/appointments', label: 'Consultas', icon: Calendar, show: canViewAppointments() },
    { path: '/slots', label: 'Vagas', icon: CalendarDays, show: canViewSlots() },
    { path: '/procedimentos', label: 'Procedimentos', icon: ClipboardList, show: canViewProcedimentos() },
    { path: '/users', label: 'Usuários', icon: Users, show: canManageUsers() },
    { path: '/reports', label: 'Relatórios', icon: FileText, show: canViewReports() },
    { path: '/audit-logs', label: 'Auditoria', icon: History, show: canViewAudit() },
    { path: '/settings', label: 'Configurações', icon: Settings, show: canViewSettings() },
    { path: '/licenses', label: 'Licenciamento', icon: Shield, show: canViewLicenses() },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-30">
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="h-20 px-4 flex items-center gap-2.5 border-b border-slate-200">
            <img src="/assets/logo_hm_dark.png" alt="HM Soluções em TI" className="h-14 w-auto rounded" />
            <div className="flex flex-col">
              <span className="text-lg font-bold text-slate-900" data-testid="app-title">ConsulTrack</span>
              <span className="text-xs text-slate-500 font-medium" data-testid="current-datetime">
                {currentDateTime.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo', weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase())} - {currentDateTime.toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => {
              if (!item.show) return null;
              
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'text-teal-700 bg-teal-50 font-medium'
                        : 'text-slate-600 hover:text-teal-700 hover:bg-teal-50'
                    }`
                  }
                  data-testid={`nav-${item.path.slice(1) || 'dashboard'}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={2} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                <span className="text-teal-700 font-semibold text-sm">
                  {user?.name?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeColor()}`}>
                  {getRoleLabel()}
                </span>
              </div>
            </div>
            <div className="space-y-2">
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                size="sm"
                className="w-full justify-start text-slate-600 hover:text-teal-700 hover:border-teal-200 hover:bg-teal-50"
                data-testid="profile-button"
              >
                <UserCircle className="h-4 w-4 mr-2" />
                Meu Perfil
              </Button>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="w-full justify-start text-slate-600 hover:text-rose-600 hover:border-rose-200 hover:bg-rose-50"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen">
        {/* Header com logo do cliente */}
        <div className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between relative">
          {/* Logo do cliente mais a esquerda */}
          <div className="flex-1 flex justify-start pl-4">
            {clientLogoUrl ? (
              <img src={clientLogoUrl} alt="Logo do Cliente" className="h-20 w-auto object-contain rounded-lg" data-testid="header-client-logo" />
            ) : (
              <div className="flex flex-col items-center">
                <img src="/assets/logo_hm_new.png" alt="HM Solucoes em TI" className="h-20 w-auto rounded-lg" />
              </div>
            )}
          </div>
          {/* Logo HM direita */}
          <div className="flex-shrink-0">
            <img src="/assets/logo_hm_new.png" alt="HM Solucoes em TI" className="h-14 w-auto rounded" />
          </div>
        </div>
        <div className="p-8">
          {/* License Suspended - Block non-master users */}
          {licenseStatus && licenseStatus.status === 'suspended' && !isMaster() ? (
            <div className="flex flex-col items-center justify-center min-h-[60vh]" data-testid="license-suspended-block">
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl p-10 max-w-lg text-center">
                <Shield className="h-16 w-16 text-red-400 mx-auto mb-4" />
                <h2 className="text-3xl font-bold text-red-800 mb-3" data-testid="system-unavailable-title">SISTEMA INDISPONÍVEL</h2>
                <p className="text-red-700 mb-4">
                  O acesso ao sistema foi temporariamente suspenso.
                </p>
                <p className="text-sm text-red-600 mb-6">
                  Entre em contato com o administrador ou com a HM Soluções em TI para regularizar a situação.
                </p>
                <Button
                  onClick={handleLogout}
                  data-testid="license-suspended-logout-btn"
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sair
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* License Warning Banner - apenas Admin Geral+ */}
              {isAdminGeral() && licenseStatus && licenseStatus.activated && (licenseStatus.status === 'grace_period' || licenseStatus.status === 'expired' || (licenseStatus.days_remaining !== undefined && licenseStatus.days_remaining <= 3)) && (
                <div className={`mb-6 rounded-lg p-4 flex items-center gap-3 ${
                  licenseStatus.status === 'expired' ? 'bg-red-50 border border-red-200' :
                  licenseStatus.status === 'grace_period' ? 'bg-orange-50 border border-orange-200' :
                  'bg-amber-50 border border-amber-200'
                }`} data-testid="license-warning-banner">
                  <AlertTriangle className={`h-5 w-5 flex-shrink-0 ${
                    licenseStatus.status === 'expired' ? 'text-red-600' :
                    licenseStatus.status === 'grace_period' ? 'text-orange-600' :
                    'text-amber-600'
                  }`} />
                  <div className="flex-1">
                    {licenseStatus.status === 'expired' && (
                      <p className="text-sm font-medium text-red-800">
                        Licenca expirada. Novas marcacoes estao bloqueadas. Entre em contato com a HM Solucoes em TI para renovar.
                      </p>
                    )}
                    {licenseStatus.status === 'grace_period' && (
                      <p className="text-sm font-medium text-orange-800">
                        Licenca expirada - periodo de carencia: {licenseStatus.grace_days_remaining} dia(s) restante(s). Renove para evitar bloqueio.
                      </p>
                    )}
                    {licenseStatus.status === 'active' && licenseStatus.days_remaining <= 3 && (
                      <p className="text-sm font-medium text-amber-800">
                        Licença expira em {licenseStatus.days_remaining} dia(s). Entre em contato para renovação.
                      </p>
                    )}
                  </div>
                </div>
              )}
              {/* Suspended banner for Master */}
              {licenseStatus && licenseStatus.status === 'suspended' && isMaster() && (
                <div className="mb-6 rounded-lg p-4 flex items-center gap-3 bg-red-50 border border-red-200" data-testid="license-suspended-banner">
                  <AlertTriangle className="h-5 w-5 flex-shrink-0 text-red-600" />
                  <p className="text-sm font-medium text-red-800">
                    Licenca suspensa. Outros usuarios estao bloqueados. Reative a licenca no painel de Licenciamento.
                  </p>
                </div>
              )}
              <PageErrorBoundary locationKey={location.key}>
                {children}
              </PageErrorBoundary>
            </>
          )}
        </div>
      </main>
    </div>
  );
};